<template>
    <reservations-wrapper
            :rows="processedReservations"
            :isLoading="isLoadingReservations"
            :hasLoadedOnce="hasLoadedOnce"
            :pageInfo="pageInfo"
            :header="header"
            :body="body"
            :paginationValue="paginationComputed"
            :resultsPerPage="resultsPerPage"
            :resultsPerPageOptions="resultsPerPageOptions"
            :allowSelectRows="allowSelectRows"
            :bulkInvoiceTotal="bulkInvoiceTotal"
            :initialTab="selectedTab"
            @page-change="onPageChange"
            @results-per-page-change="onResultsPerPageChange"
        >
</reservations-wrapper>
</template>

<script>
    import mixin from '@/mixins/reservationsLogicMixin'
    import List from '@/tools/list/baseList'
    import Loader from '@/tools/loaders/loader1'
    import Export from '@/tools/ExportV2'
    import SellerPicker from '@/tools/SellerPicker'
    import Multiselect from 'vue-multiselect'
    import VueDatepicker from 'vuejs-datepicker'
    import extendedVSelect from '@/extends/vue-select'
    import _ from 'lodash'
    import ReservationsTabs from '@/components/reservations/ReservationsTabs'
    import ReservationsWrapper from '@/components/reservations/ReservationsWrapper'
    import ReservationsSearchBar from '@/components/reservations/ReservationsSearchBar'
    import { reservationCheckinStatus } from '@/utils/reservationRowClass'
    import { RESERVATION_LIST_TYPE } from '@/enums/reservationListType'

    export default {
        name: 'ReservationsByDepartureDate',
        mixins: [ mixin ],
        props: {
            reservationMainFilters: {
                type: Object,
                required: true
            },
            pagination: {
                type: Object,
                default: () => ({ currentPage: 1 })
            },
            selectedTab: {
                type: String,
                default: 'reservations'
            }
        },
        data () {
            return {
                config: {
                    viewId: RESERVATION_LIST_TYPE.DEPARTURE_DATE,
                    listUrl: '/api/booking/DeparturesList',
                    downloadUrl: '/api/booking/DeparturesDownload'
                },
                hasLoadedOnce: false
            }
        },
        methods : {
            onResultsPerPageChange(newSize) {
                this.resultsPerPage = newSize;
                this.resultsPerPageChanged();
            },
            getReservationsPaginated: _.debounce(function() {
                this.$set(this, 'bulkSelectAll', false);

                 if (this.isLoadingReservations) return;

                this.isLoadingReservations = true;
                    
                axios.get(this.config.listUrl, {
                    params: this.requestFilterOptions
                }).then(response => {
                    if (response.status == 200) {
                        this.pageInfo = response.data.pageInfo;
                        this.reservationsArr = this.selectSelectedReservations(response.data.result);
                    }
                })
                .catch(() => {})
                .finally(() => {
                    this.isLoadingReservations = false;
                    this.hasLoadedOnce = true;
                });
            }, 500)
        },
        computed : {
            processedReservations() {
                return this.reservationsArr.map(reservation => ({
                    ...reservation,
                    disabledCheckbox: reservation.paymentStatus !== 1
                }))
            },
            requestFilterOptions () {
                const reservationsFilters = this.getReservationsFilters;

                Object.assign(this.filters, {
                    resourceId: reservationsFilters.resourceId,
                    paymentStatus: reservationsFilters.paymentStatus,
                    countryId: reservationsFilters.countryId,
                    status: reservationsFilters.status,
                    currentCheckboxPickup: reservationsFilters.currentCheckboxPickup,
                    currentCheckboxNote: reservationsFilters.currentCheckboxNote,
                    categories: reservationsFilters.categories
                });

                return {
                    page: this.pagination.currentPage,
                    pageSize: this.resultsPerPage,
                    's.String': this.filters.searchString,
                    's.ProductId': this.filters.productId && this.filters.productId.length > 0 ? this.filters.productId.map(item => item.id) : null,
                    's.ResourceId': this.filters.resourceId && this.filters.resourceId.length > 0 ? this.filters.resourceId.map(item => item.id) : null,
                    's.locations': this.filters.locations && this.filters.locations.length > 0 ? this.filters.locations.map(item => item.name) : null,
                    's.audienceTypes': this.filters.categories && this.filters.categories.length > 0 ? this.filters.categories.map(item => item.name) : null,
                    's.PaymentStatus': this.filters.paymentStatus && this.filters.paymentStatus.length > 0 ? this.filters.paymentStatus.map(item => item.id) : null,
                    's.StatusList': this.filters.status && this.filters.status.length > 0 ? this.filters.status.map(item => item.id) : null,
                    's.isNoFullCheckin': this.filters.currentCheckboxNoCheckin ? true : null,
                    's.withTransport': this.filters.currentCheckboxPickup ? true : null,
                    's.withNote': this.filters.currentCheckboxNote ? true : null,
                    's.eventId': this.filters.eventId,
                    's.dateStart': (this.filters.eventId) ? null : this.reservationMainFilters.dateRange[0],
                    's.dateEnd': (this.filters.eventId) ? null : this.reservationMainFilters.dateRange[1],
                    's.countryId': this.filters.countryId,
                    's.sellerType': this.filters.sellerType,
                    's.channelType': this.filters.channelType,
                    's.sellerId': this.filters.sellerId
                }
            },
            header() {
                return {
                    selection: {
                        active: this.allowSelectRows,
                        event: (event) => {
                            this.bulkSelectAll = event
                            this.selectCurrentPage(event)
                        }
                    },
                    selectAll: this.bulkSelectAll,
                    list: {
                        productColorSign: ' ',
                        reservation: 'Reservation',
                        resource: 'Resource',
                        departure: 'Departure',
                        pax: 'PAX',
                        totalPrice: 'Amount',
                        statusReservation: 'Status',
                        statusPayment: 'Payment',
                        fullName: 'Client Name',
                        seller: 'Seller',
                        extras: 'Extras',
                        checkin: 'Check-In | No Show',
                        icons: '',
                    },
                    customClassCell: {},
                };
            },
            body() {
                //let vm = this;
                return {
                    selection: {
                        active: this.allowSelectRows,
                        event: (event, reservation) => {
                            if (this.getBulkSelectedIds.length > 0 && this.getBulkSelectedIds.includes(reservation.id)) {
                                this.removeFromBulkSelectedIds([reservation.id])
                            } else {
                                this.addToBulkSelectedIds([reservation.id])
                            }
                        }
                    },
                    customRowEvent: (reservation) => {this.view(reservation.id) },
                    customCells: {
                        productColorSign: this.cellProductColorSign,
                        reservation: this.cellReservation,
                        resource: this.cellResource,
                        departure: this.cellDeparture,
                        created: this.cellCreated,
                        pax: this.cellPax,
                        totalPrice: this.cellTotalPrice,
                        statusReservation: this.cellStatusReservation,
                        statusPayment: this.cellStatusPayment,
                        fullName: this.cellFullName,
                        seller: this.cellSeller,
                        extras: this.cellExtras,
                        checkin: this.cellCheckin,
                        icons: this.cellIcons
                    },
                    customRowClass: (reservation) => reservationCheckinStatus(reservation)
                }
            },
        },
        components: { 
            List, 
            Loader, 
            Export, 
            SellerPicker, 
            Multiselect, 
            VueDatepicker, 
            vSelect: extendedVSelect,
            ReservationsTabs,
            ReservationsWrapper,
            ReservationsSearchBar
        }
    }
</script>

<style lang="css" scoped>
</style>



// WEBPACK FOOTER //
// ReservationsByDepartureDate.vue?0ed2951b